import {
  Injectable,
  EnvironmentInjector,
  inject,
  runInInjectionContext,
} from "@angular/core";
import { Functions, httpsCallable } from "@angular/fire/functions";

/**
 * Thin wrapper over modular Firebase callable Functions — the replacement for
 * the global compat `firebase.functions()` accessor. It deliberately mirrors
 * that API surface (`.httpsCallable(name)` → a callable taking the request data
 * and resolving to `{ data }`) so the ~50 existing call sites keep their exact
 * shape; only the source of `.httpsCallable` swaps from `firebase.functions()`
 * to this injected service.
 *
 * The modular `httpsCallable` is zone-wrapped (calls inject(NgZone)), so both
 * building and invoking it must run inside an injection context — same pattern
 * as the Database/Auth/Storage migrations.
 */
@Injectable({ providedIn: "root" })
export class FunctionsService {
  private readonly fns = inject(Functions);
  private readonly injector = inject(EnvironmentInjector);

  /**
   * Live-tournament interactive ops consolidated server-side into the single
   * warm `tournamentOp` dispatcher (functions/dispatch.js). Calls to these names
   * are transparently rewritten to invoke `tournamentOp` with `{ ...data, op }`,
   * so one `minInstances: 1` instance keeps the whole cluster warm instead of
   * paying for a warm instance per op. Call sites in api.service.ts stay
   * unchanged. Keep in sync with OP_MAP in functions/dispatch.js. Empty this set
   * to roll back to per-function callables.
   */
  private static readonly DISPATCH_OPS = new Set<string>([
    "tournamentRegister",
    "tournamentUnregister",
    "movePlayer",
    "drawSeats",
    "advanceShootoutRound",
    "breakTable",
    "balanceTables",
    "addTable",
    "updateSeatingParams",
    "tournamentRebuy",
    "tournamentAddon",
    "tournamentReentry",
    "countElimination",
    "adjustActPlayers",
    "tournamentBonusChips",
    "tournamentSetBounty",
    "tournamentClearBounties",
    "eliminatePlayer",
    "drawMysteryBounty",
    "drawMysteryBountyBatch",
    "logUndo",
    "profUndoElimination",
    "profSetFinish",
    "swapFinish",
    "deleteTournament",
    "markIncomplete",
    "updateTournamentSettings",
  ]);

  /**
   * Mirrors `firebase.functions().httpsCallable(name)`. Returns `Promise<any>`
   * (not the typed `HttpsCallableResult`) to preserve the loose `any` contract
   * the old compat `firebase.functions()` accessor gave every call site — many
   * read `result.data` on api methods whose `.catch` would otherwise widen the
   * type to `… | void`.
   */
  httpsCallable(name: string): (data?: any) => Promise<any> {
    if (FunctionsService.DISPATCH_OPS.has(name)) {
      // `op` is appended last so it stays authoritative even if a payload ever
      // carried its own `op` field.
      return (data?: any) =>
        this.callWithTransportRetry(() =>
          runInInjectionContext(this.injector, () =>
            httpsCallable(
              this.fns,
              "tournamentOp"
            )({ ...(data || {}), op: name })
          )
        );
    }
    return (data?: any) =>
      this.callWithTransportRetry(() =>
        runInInjectionContext(this.injector, () =>
          httpsCallable(this.fns, name)(data)
        )
      );
  }

  // A request that died at the TRANSPORT layer never reached the function —
  // retry it once after a beat. Old WebKit (iPads/iPhones) suspends sockets
  // when the tab is backgrounded, so the first call after resuming reliably
  // fails and the immediate retry succeeds; without this the user sees the
  // generic error toast and has to press the button twice. The SDK reports
  // transport failures as "functions/internal" with no `details`; genuine
  // server-thrown errors carry specific codes (failed-precondition etc.) or
  // details and are NOT retried. Worst case equals the user re-clicking.
  private callWithTransportRetry(
    call: () => Promise<any>
  ): Promise<any> {
    const isTransportError = (error: any): boolean => {
      const code = error?.code || "";
      const msg = String(error?.message || "");
      if (code === "functions/internal" && error?.details == null) return true;
      return /network|fetch|load failed|connection/i.test(msg);
    };
    return call().catch((error: any) => {
      if (!isTransportError(error)) throw error;
      console.warn("callable transport failure — retrying once", error?.code);
      return new Promise((resolve) => setTimeout(resolve, 400)).then(call);
    });
  }
}
